import {
  Modal,
  Dialog,
  Button,
  Field,
  Input,
  RadioButton,
} from '@oliasoft-open-source/react-ui-library';
import { useState } from 'react';
import translations from 'client/internationalization/translation-map.json';
import { useTranslation } from 'react-i18next';
import { HierarchyLevelType } from 'common/interfaces/hierarchy-entities';
import { IHierarchyTreeItem } from '../hierarchy/hierarchy.interfaces';
import { hierarchyTypeAddLabel, hierarchyTypeEditLabel, hierarchyTypeNewLabel } from '../hierarchy/hierarchy.utils';
import { WellStatusType } from 'common/enums/entity-enums';

export type EntityFormModalProps = {
  entity: Partial<IHierarchyTreeItem>;
  isEdit: boolean;
  saveEntity: (entity: { type: HierarchyLevelType, payload: Partial<IHierarchyTreeItem> }) => void;
  close: () => void;
};

interface IRadioButtonOption {
    label: string | number;
    value: string | number;
    name?: string;
}

export const EntityFormModal = ({
  entity,
  isEdit,
  saveEntity,
  close,
}: EntityFormModalProps) => {
  const [editingItem, setEditingItem] = useState(() => {
    if (entity.entityType === HierarchyLevelType.Design && !entity.well_status) {
      return { ...entity, well_status: WellStatusType.Prototype };
    }
    return entity;
  });
  const { t } = useTranslation();

  const getDesignCategoryOption = (option: WellStatusType) => ({ name: 'well_status', value: option, label: option });
  const radioButtonsDesignCategory = Object.values(WellStatusType).reduce((options, v) => {
    if (v !== WellStatusType.Archived) {
      options.push(getDesignCategoryOption(v));
    }
    return options;
  }, [] as IRadioButtonOption[]);
  const [designCategory, setDesignCategory] = useState<string>(editingItem.well_status || getDesignCategoryOption(WellStatusType.Prototype).value);
  const selectedDesignCategoryOption = radioButtonsDesignCategory.find(radioOption => {
    return radioOption.value === designCategory;
  }) || getDesignCategoryOption(WellStatusType.Prototype);

  const validateData = (): boolean => Boolean(editingItem.name);
  const { entityType } = entity;
  if (!entityType) return null;
  return (
    <Modal
      centered
      visible
    >
      <Dialog
        dialog={{
          content: (
            <>
              <Field label={t(translations.name)}>
                <Input
                  value={editingItem.name}
                  onChange={
                    (event: React.ChangeEvent<{ value: string }>) => setEditingItem({ ...editingItem, name: event.target.value })
                  }
                  placeholder={hierarchyTypeNewLabel[entityType]}
                />
              </Field>

              {entityType === HierarchyLevelType.Design && (
                <>
                  <Field label={t(translations.designType)}>
                    <RadioButton
                      options={radioButtonsDesignCategory}
                      value={selectedDesignCategoryOption!}
                      onChange={(evt) => {
                        setDesignCategory(evt.target.value);
                        setEditingItem({ ...editingItem, well_status: evt.target.value });
                      }}
                    />
                  </Field>
                </>
              )}
            </>
          ),
          footer: (
            <>
              <Button
                colored
                type="submit"
                disabled={!validateData()}
                label={isEdit ? t(translations.ok) : t(translations.add)}
                onClick={() => saveEntity({ type: entityType, payload: editingItem })}
              />
              <Button
                label={t(translations.cancel)}
                onClick={close}
              />
            </>
          ),
          heading: isEdit ? hierarchyTypeEditLabel[entityType] : hierarchyTypeAddLabel[entityType],
          width: '300px',
        }}
      />
    </Modal>
  );
};
